import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { Aviso, Botao, Etiqueta, Modal } from '../../componentes/ui.jsx';
import { baixarArquivo, gerarPdfTabela } from '../../lib/pdfTabela.js';
import './importar.css';

/**
 * Importa a agenda semanal da Hapvida (PDF "CONSULTAS MARCADAS").
 *
 * Tres passos, e nada e gravado antes do ultimo:
 *   1. escolher o PDF;
 *   2. ver a previa: quantas consultas por medico, quais sao novas, quem ficou
 *      sem telefone, o que o Aquiles revisou;
 *   3. confirmar — as consultas entram na agenda (Hapvida, sem preco) e os
 *      pacientes recebem o lembrete de vespera como os demais.
 *
 * Reimportar o mesmo arquivo nao duplica: o servidor reconhece cada consulta.
 */

const dataCurta = (iso) => iso.split('-').reverse().join('/');

/**
 * PDF com as consultas que ficaram de fora por falta de telefone, para a
 * recepcao conferir no sistema da Hapvida e cadastrar a mao. Gerado no
 * navegador: a lista tem dado de paciente e nao precisa ir a lugar nenhum.
 */
function baixarSemTelefone(previa) {
  const lista = [...previa.semTelefone].sort((a, b) => `${a.data} ${a.hora}`.localeCompare(`${b.data} ${b.hora}`));
  const periodo = previa.periodo ? `Período do PDF: ${previa.periodo}. ` : '';
  const blob = gerarPdfTabela({
    titulo: `Hapvida: ${lista.length} consulta(s) sem telefone válido`,
    subtitulo: `${periodo}Gerado em ${new Date().toLocaleString('pt-BR')}.`,
    observacao: 'Estas consultas NÃO foram importadas. Confira o telefone no sistema da Hapvida e marque/cadastre o paciente pela Agenda.',
    colunas: [
      { titulo: '#', largura: 0.04 },
      { titulo: 'Data', largura: 0.09 },
      { titulo: 'Hora', largura: 0.06 },
      { titulo: 'Paciente', largura: 0.3 },
      { titulo: 'Médico', largura: 0.2 },
      { titulo: 'Retorno', largura: 0.07 },
      { titulo: 'Código', largura: 0.09 },
      { titulo: 'Telefone no PDF', largura: 0.15 }
    ],
    linhas: lista.map((c, i) => [
      i + 1,
      dataCurta(c.data),
      c.hora,
      c.nome,
      c.profissional ?? '',
      c.retorno ? 'Sim' : 'Não',
      c.codigo ?? '',
      c.numerosNoPdf?.length ? `${c.numerosNoPdf.join(', ')} (incompleto)` : 'em branco'
    ])
  });
  baixarArquivo(blob, `hapvida-sem-telefone-${new Date().toISOString().slice(0, 10)}.pdf`);
}

const SITUACAO = {
  nova: ['Nova', 'sucesso'],
  ja_importada: ['Já importada', 'neutro'],
  sem_telefone: ['Sem telefone', 'alerta'],
  sem_profissional: ['Médico não encontrado', 'perigo']
};

function lerComoDataUrl(arquivo) {
  return new Promise((ok, erro) => {
    const leitor = new FileReader();
    leitor.onload = () => ok(leitor.result);
    leitor.onerror = () => erro(new Error('Não consegui ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}

export function ImportarHapvida({ aoFechar }) {
  const queryClient = useQueryClient();
  const [arquivo, setArquivo] = useState(null);
  // "Cadastrar sem numero": as consultas sem telefone tambem entram (contato sem numero).
  const [semNumero, setSemNumero] = useState(false);

  const analisar = useMutation({
    mutationFn: async (f) => api.post('/api/agenda/importacoes/hapvida', { arquivo: await lerComoDataUrl(f) })
  });
  const confirmar = useMutation({
    mutationFn: (id) => api.post(`/api/agenda/importacoes/hapvida/${id}/confirmar`, { incluirSemNumero: semNumero }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agenda'] })
  });

  const previa = analisar.data;
  const feito = confirmar.data;
  const aImportar = previa ? previa.novas + (semNumero ? previa.semTelefone.length : 0) : 0;

  return (
    <Modal
      titulo="Importar agenda da Hapvida"
      aberto
      largura={720}
      aoFechar={aoFechar}
      rodape={
        feito ? (
          <Botao onClick={aoFechar}>Fechar</Botao>
        ) : previa ? (
          <>
            <Botao
              variante="secundario"
              onClick={() => {
                analisar.reset();
                setSemNumero(false);
              }}
              disabled={confirmar.isPending}
            >
              Escolher outro arquivo
            </Botao>
            <Botao onClick={() => confirmar.mutate(previa.id)} carregando={confirmar.isPending} disabled={!aImportar}>
              Importar {aImportar} consulta{aImportar === 1 ? '' : 's'}
            </Botao>
          </>
        ) : (
          <>
            <Botao variante="secundario" onClick={aoFechar}>
              Cancelar
            </Botao>
            <Botao onClick={() => arquivo && analisar.mutate(arquivo)} carregando={analisar.isPending} disabled={!arquivo}>
              Ler o PDF
            </Botao>
          </>
        )
      }
    >
      {!previa && !feito && (
        <div className="imp-escolher">
          <p className="texto-fraco">
            Envie o relatório semanal <strong>“CONSULTAS MARCADAS”</strong> da Hapvida em PDF. O sistema lê o arquivo,
            mostra uma prévia e só grava quando você confirmar.
          </p>
          <label className="imp-arquivo">
            <input type="file" accept="application/pdf,.pdf" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} />
            <span>{arquivo ? arquivo.name : 'Escolher o PDF…'}</span>
          </label>
          {analisar.isPending && <p className="texto-fraco">Lendo o PDF… O Aquiles revisa as linhas que o sistema não conseguiu ler sozinho.</p>}
          {analisar.isError && <Aviso tom="perigo">{analisar.error.message}</Aviso>}
        </div>
      )}

      {previa && !feito && (
        <div className="imp-previa">
          <div className="imp-numeros">
            <div><strong>{previa.total}</strong><span>consultas no PDF</span></div>
            <div><strong>{previa.novas}</strong><span>novas</span></div>
            <div><strong>{previa.jaImportadas}</strong><span>já importadas</span></div>
            <div><strong>{previa.contatosNovos}</strong><span>contatos novos</span></div>
          </div>
          {previa.periodo && <p className="texto-fraco">Período: {previa.periodo}</p>}

          <table className="imp-tabela">
            <thead>
              <tr><th>Médico no PDF</th><th>No sistema</th><th>Consultas</th><th>Novas</th></tr>
            </thead>
            <tbody>
              {previa.porProfissional.map((p) => (
                <tr key={p.prestador}>
                  <td>{p.prestador}</td>
                  <td>{p.profissional ?? <Etiqueta tom="perigo">não encontrado</Etiqueta>}</td>
                  <td>{p.total}</td>
                  <td>{p.novas}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {previa.comVariosTelefones > 0 && (
            <Aviso tom="info">
              {previa.comVariosTelefones} paciente(s) com mais de um telefone: o primeiro celular vira o principal e os
              outros ficam guardados no mesmo contato (quem escrever de qualquer um deles cai na mesma ficha).
            </Aviso>
          )}
          {previa.semProfissional.length > 0 && (
            <Aviso tom="perigo">
              Médico não encontrado na Equipe: {previa.semProfissional.join(', ')}. Essas consultas não serão importadas —
              cadastre o médico (ou confira o nome) e envie o PDF de novo.
            </Aviso>
          )}
          {previa.semTelefone.length > 0 && (
            <Aviso tom="alerta">
              {previa.semTelefone.length} consulta(s) sem telefone válido não serão importadas:{' '}
              {previa.semTelefone.slice(0, 6).map((c) => `${c.nome} (${c.data.split('-').reverse().slice(0, 2).join('/')} ${c.hora})`).join(', ')}
              {previa.semTelefone.length > 6 ? '…' : ''}
              <div className="linha" style={{ marginTop: 8 }}>
                <Botao variante="secundario" tamanho="sm" onClick={() => baixarSemTelefone(previa)}>
                  Baixar PDF para conferir
                </Botao>
                <Botao variante={semNumero ? 'primario' : 'secundario'} tamanho="sm" aria-pressed={semNumero} onClick={() => setSemNumero((v) => !v)}>
                  {semNumero ? '✓ Serão cadastradas sem número' : 'Cadastrar sem número'}
                </Botao>
              </div>
              {semNumero && (
                <p className="texto-fraco" style={{ marginTop: 6 }}>
                  Entram na agenda num contato <strong>sem número</strong> (sem lembrete nem campanha). Quando o paciente escrever no
                  WhatsApp dizendo que tem consulta pela Hapvida, a Sofia pede o nome completo e junta o cadastro ao número dele.
                </p>
              )}
            </Aviso>
          )}
          {previa.telefonesInvalidos.length > 0 && (
            <p className="texto-fraco">
              {previa.telefonesInvalidos.length} número(s) no PDF parecem incompletos (dígito faltando) e foram ignorados.
            </p>
          )}
          {previa.revisao.usada ? (
            <p className="texto-fraco">O Aquiles revisou {previa.revisao.revisadas} linha(s) que o sistema não leu sozinho.</p>
          ) : previa.naoLidas > 0 ? (
            <Aviso tom="alerta">{previa.naoLidas} linha(s) do PDF não puderam ser lidas{previa.revisao.erro ? ' (a revisão do Aquiles não respondeu)' : ''}.</Aviso>
          ) : null}

          <p className="imp-subtitulo">Primeiras consultas</p>
          <ul className="imp-amostra">
            {previa.amostra.map((c, i) => {
              const [rotulo, tom] = SITUACAO[c.situacao] ?? [c.situacao, 'neutro'];
              return (
                <li key={i}>
                  <span className="mono">{c.data.split('-').reverse().slice(0, 2).join('/')} {c.hora}</span>
                  <span className="crescer">{c.nome}</span>
                  <span className="texto-fraco">{c.profissional ?? '—'}</span>
                  <Etiqueta tom={tom}>{rotulo}</Etiqueta>
                </li>
              );
            })}
          </ul>
          {confirmar.isError && <Aviso tom="perigo">{confirmar.error.message}</Aviso>}
        </div>
      )}

      {feito && (
        <div className="imp-previa">
          <Aviso tom={feito.erros.length ? 'alerta' : 'sucesso'}>
            {feito.criadas} consulta(s) importada(s), {feito.contatosNovos} contato(s) novo(s) e {feito.pacientesNovos}{' '}
            paciente(s) novo(s).{feito.semNumero ? ` ${feito.semNumero} sem número (aguardando o paciente escrever).` : ''}{feito.erros.length ? ` ${feito.erros.length} não entraram (veja abaixo).` : ''}
          </Aviso>
          {feito.erros.length > 0 && (
            <ul className="imp-amostra">
              {feito.erros.slice(0, 10).map((e, i) => (
                <li key={i}>
                  <span className="crescer">{e.nome}</span>
                  <span className="texto-fraco">{e.erro}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
